<template>
  <section class="py-12 bg-slate-50 dark:bg-slate-900/50" id="menu" aria-labelledby="menu-heading-t5">
    <div class="max-w-7xl mx-auto px-4 sm:px-6">
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 id="menu-heading-t5" class="rms-t5-heading">{{ sectionTitle || $t('public.viewMenu') }}</h2>
          <p class="text-xs text-slate-500 dark:text-slate-400 font-medium">{{ $t('public.t3ExquisiteSelections') }}</p>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="(group, gIdx) in displayGroups"
            :key="gIdx"
            type="button"
            class="filter-btn px-3 py-2 text-xs font-semibold border rounded min-h-[44px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 flex items-center gap-2"
            :class="activeGroupIndex === gIdx ? 'border-[var(--rms-t5-primary,#2563eb)] bg-[var(--rms-t5-primary,#2563eb)]/10 text-[var(--rms-t5-primary,#2563eb)]' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'"
            :style="{ '--tw-ring-color': 'var(--rms-t5-primary,#2563eb)' }"
            @click="onGroupTabClick(group, gIdx)"
          >
            <span class="material-symbols-outlined text-sm">category</span>
            {{ group.category_name }}
          </button>
        </div>
      </div>
      <p
        v-if="displayGroups.length && activeGroup?.category_description"
        class="text-sm text-slate-600 dark:text-slate-400 mb-4 max-w-3xl leading-relaxed"
      >
        {{ activeGroup.category_description }}
      </p>
      <p v-if="!displayGroups.length" class="text-slate-600 dark:text-slate-400 text-center py-12">{{ emptyMessage || $t('public.noMenuItems') }}</p>
      <template v-else>
        <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden rounded-lg">
          <div class="overflow-x-auto">
            <table class="w-full text-left overflow-x-scroll border-collapse min-w-[640px]" role="table" aria-labelledby="menu-heading-t5">
              <thead class="bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-[11px] font-bold uppercase tracking-wider">
                <tr>
                  <th class="px-3 sm:px-4 py-3 border-r border-slate-200 dark:border-slate-700 w-14 sm:w-16 text-center" scope="col">{{ $t('public.t5TableImage') }}</th>
                  <th class="px-3 sm:px-4 py-3 border-r border-slate-200 dark:border-slate-700" scope="col">{{ $t('public.t5TableProduct') }}</th>
                  <th class="hidden sm:table-cell px-3 sm:px-4 py-3 border-r border-slate-200 dark:border-slate-700 text-right" scope="col">{{ $t('public.t5TablePrice') }}</th>
                  <th class="hidden sm:table-cell px-3 sm:px-4 py-3 w-24 sm:w-28 text-center" scope="col">{{ $t('public.t5TableAction') }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100 dark:divide-slate-700/70 text-sm">
                <template v-if="activeGroup && activeGroup.items.length">
                  <tr
                    v-for="item in activeGroup.items"
                    :key="item.uuid"
                    class="data-row transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    :class="{ 'rms-item-unavailable': !isItemClickable(item, activeGroup) }"
                  >
                    <td class="px-3 sm:px-4 py-3 align-middle text-center border-r border-slate-200 dark:border-slate-700">
                      <div class="w-10 h-10 sm:w-12 sm:h-12 mx-auto flex items-center justify-center border border-slate-200 dark:border-slate-600 rounded overflow-hidden bg-slate-50 dark:bg-slate-800">
                        <img
                          v-if="itemCardDisplayImageUrl(item)"
                          :src="itemCardDisplayImageUrl(item)"
                          :alt="item.name || ''"
                          loading="lazy"
                          class="w-full h-full object-cover"
                        >
                        <span
                          v-else
                          class="material-symbols-outlined text-2xl"
                          :style="{ color: 'var(--rms-t5-primary,#2563eb)' }"
                          aria-hidden="true"
                        >inventory_2</span>
                      </div>
                    </td>
                    <td class="px-3 sm:px-4 py-3 align-middle sm:border-r border-slate-200 dark:border-slate-700 relative">
                      <PublicMenuItemOwnerEditFab
                        v-if="showOwnerEditFab"
                        :item-uuid="item.catalog_item_uuid || item.uuid"
                        :visible="showOwnerEditFab"
                      />
                      <div>
                        <div
                          class="font-bold text-slate-900 dark:text-slate-100"
                          :class="item.is_available === false ? 'text-slate-500 dark:text-slate-500' : ''"
                        >
                          {{ item.name || 'Untitled' }}
                        </div>
                        <p v-if="item.description" class="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">{{ item.description }}</p>
                        <div v-if="itemTags(item).length" class="flex flex-wrap gap-1 mt-1.5">
                          <span
                            v-for="tag in itemTags(item)"
                            :key="tag.uuid"
                            class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
                            :style="tagStyle(tag)"
                            :title="tag.text"
                            role="img"
                            :aria-label="tag.text"
                          >
                            <span class="material-symbols-outlined text-xs" style="color: var(--tag-fg)" aria-hidden="true">{{ tag.icon || 'label' }}</span>
                            <span v-if="tag.text" class="whitespace-nowrap" style="color: var(--tag-fg)">{{ tag.text }}</span>
                          </span>
                        </div>
                        <ul v-if="item.type === 'combo' && comboEntries(item).length" class="mt-1 list-none text-xs text-slate-500 dark:text-slate-400 space-y-0.5" aria-label="Combo contents">
                          <li v-for="(entry, eIdx) in comboEntries(item)" :key="eIdx">
                            {{ entry.name }} × {{ entry.quantity }}{{ entry.modifier_label ? ` (${entry.modifier_label})` : '' }}
                          </li>
                        </ul>
                        <div
                          v-if="item.type === 'with_variants' && variantSkus(item).length && variantOptionGroups(item).length"
                          class="mt-2 space-y-2 max-w-md"
                          @click.stop
                        >
                          <div
                            v-for="optGroup in variantOptionGroups(item)"
                            :key="`${item.uuid}-${optGroup.name}`"
                            class="space-y-0.5"
                          >
                            <label
                              class="block text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400"
                              :for="variantSelectId('rms-t5', item, optGroup.name)"
                            >
                              {{ optGroup.name || $t('public.option') }}
                            </label>
                            <select
                              :id="variantSelectId('rms-t5', item, optGroup.name)"
                              class="rms-t5-variant-select w-fit max-w-full sm:w-full min-h-[40px] rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-2 py-1.5 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rms-t5-primary,#2563eb)] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
                              :value="variantSelectionValue(item, optGroup.name)"
                              :disabled="!isItemClickable(item, activeGroup)"
                              :aria-label="`${optGroup.name || $t('public.option')}: ${item.name || ''}`"
                              @change="onVariantGroupChange(item, optGroup.name, $event.target.value)"
                            >
                              <option
                                v-for="val in (optGroup.values || [])"
                                :key="String(val)"
                                :value="val"
                                :disabled="!isVariantGroupValueOrderable(item, optGroup.name, val)"
                              >
                                {{ val }}{{ !isVariantGroupValueOrderable(item, optGroup.name, val) ? ' (' + $t('public.variantOptionNotAvailable') + ')' : '' }}
                              </option>
                            </select>
                          </div>
                        </div>
                        <p v-else-if="item.type === 'with_variants' && variantSkus(item).length && !variantOptionGroups(item).length" class="text-slate-500 dark:text-slate-400 text-xs mt-1">
                          {{ variantOptionGroupsSummary(item) }}
                        </p>
                        <div class="sm:hidden mt-2 space-y-2">
                          <div>
                            <span
                              v-if="item.type !== 'with_variants' && item.is_available !== false && itemPrice(item) != null"
                              class="font-bold text-sm"
                              :style="{ color: 'var(--rms-t5-primary,#2563eb)' }"
                            >
                              {{ formatPrice(itemPrice(item)) }}
                            </span>
                            <span
                              v-else-if="item.type === 'with_variants' && variantOptionGroups(item).length && item.is_available !== false && isVariantSkuPubliclyOrderable(resolvedVariantSku(item)) && resolvedVariantSku(item)?.price != null"
                              class="font-bold text-sm"
                              :style="{ color: 'var(--rms-t5-primary,#2563eb)' }"
                            >
                              {{ formatPrice(resolvedVariantSku(item).price) }}
                            </span>
                            <span
                              v-else-if="item.type === 'with_variants' && !variantOptionGroups(item).length && variantMinPrice(item) != null"
                              class="font-semibold text-sm"
                              :style="{ color: 'var(--rms-t5-primary,#2563eb)' }"
                            >
                              {{ $t('public.fromPrice', { price: formatPrice(variantMinPrice(item)) }) }}
                            </span>
                            <span v-else-if="item.is_available === false" class="text-slate-500 dark:text-slate-500 text-xs">{{ $t('public.notAvailable') }}</span>
                            <span v-else class="text-slate-400 dark:text-slate-500 text-sm">—</span>
                          </div>
                          <button
                            type="button"
                            data-testid="public-menu-item-mobile"
                            class="w-fit max-w-full rms-t5-table-action px-3 py-2 bg-[var(--rms-t5-primary,#2563eb)] text-white text-xs font-bold rounded hover:opacity-90 min-h-[44px] transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rms-t5-primary,#2563eb)] focus-visible:ring-offset-2 cursor-pointer"
                            :aria-disabled="!isItemClickable(item, activeGroup)"
                            @click="isItemClickable(item, activeGroup) ? onItemClick(item, activeGroup.category_name, $event) : showUnavailableToast(activeGroup, item, now)"
                          >
                            {{ $t('public.t5ViewDetails') }}
                          </button>
                        </div>
                      </div>
                    </td>
                    <td class="hidden sm:table-cell px-3 sm:px-4 py-3 align-middle border-r border-slate-200 dark:border-slate-700 text-right">
                      <span
                        v-if="item.type !== 'with_variants' && item.is_available !== false && itemPrice(item) != null"
                        class="font-bold"
                        :style="{ color: 'var(--rms-t5-primary,#2563eb)' }"
                      >
                        {{ formatPrice(itemPrice(item)) }}
                      </span>
                      <span
                        v-else-if="item.type === 'with_variants' && variantOptionGroups(item).length && item.is_available !== false && isVariantSkuPubliclyOrderable(resolvedVariantSku(item)) && resolvedVariantSku(item)?.price != null"
                        class="font-bold"
                        :style="{ color: 'var(--rms-t5-primary,#2563eb)' }"
                      >
                        {{ formatPrice(resolvedVariantSku(item).price) }}
                      </span>
                      <span
                        v-else-if="item.type === 'with_variants' && !variantOptionGroups(item).length && variantMinPrice(item) != null"
                        class="font-semibold text-sm"
                        :style="{ color: 'var(--rms-t5-primary,#2563eb)' }"
                      >
                        {{ $t('public.fromPrice', { price: formatPrice(variantMinPrice(item)) }) }}
                      </span>
                      <span v-else-if="item.is_available === false" class="text-slate-500 dark:text-slate-500 text-xs">{{ $t('public.notAvailable') }}</span>
                      <span v-else class="text-slate-400 dark:text-slate-500">—</span>
                    </td>
                    <td class="hidden sm:table-cell px-3 sm:px-4 py-3 align-middle">
                      <div class="flex justify-center">
                        <button
                          type="button"
                          data-testid="public-menu-item"
                          class="rms-t5-table-action px-3 py-1.5 bg-[var(--rms-t5-primary,#2563eb)] text-white text-[10px] font-bold rounded hover:opacity-90 min-h-[44px] transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rms-t5-primary,#2563eb)] focus-visible:ring-offset-2 cursor-pointer"
                          :aria-disabled="!isItemClickable(item, activeGroup)"
                          @click="isItemClickable(item, activeGroup) ? onItemClick(item, activeGroup.category_name, $event) : showUnavailableToast(activeGroup, item, now)"
                        >
                          {{ $t('public.t5ViewDetails') }}
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <p v-if="activeGroup && !activeGroup.items.length" class="text-slate-600 dark:text-slate-400 text-center py-10 px-4">{{ emptyMessage || $t('public.noMenuItems') }}</p>
        </div>
      </template>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import { usePublicUnavailableToast } from '@/composables/usePublicUnavailableToast'
import { usePublicMenuDisplay } from '@/composables/usePublicMenuDisplay'
import { usePublicMenuVariantSelect } from '@/composables/usePublicMenuVariantSelect'
import PublicMenuItemOwnerEditFab from '@/components/public/PublicMenuItemOwnerEditFab.vue'
import { isAvailableNow } from '@/utils/availability'
import { isVariantSkuPubliclyOrderable } from '@/utils/publicVariantSku'

const emit = defineEmits(['select-item'])
const { showUnavailableToast, showUnavailableCategoryToast } = usePublicUnavailableToast()

const props = defineProps({
  menuGroups: { type: Array, default: () => [] },
  menuItems: { type: Array, default: () => [] },
  currency: { type: String, default: 'USD' },
  showCurrency: { type: Boolean, default: true },
  sectionTitle: { type: String, default: '' },
  emptyMessage: { type: String, default: '' },
  showOwnerEditFab: { type: Boolean, default: false },
})

const {
  displayGroups,
  formatPrice,
  itemPrice,
  itemTags,
  comboEntries,
  variantOptionGroupsSummary,
  variantOptionGroups,
  variantSkus,
  itemCardImageUrl,
} = usePublicMenuDisplay(props)

const activeGroupIndex = ref(0)
const activeGroup = computed(() => displayGroups.value[activeGroupIndex.value] ?? null)
const now = ref(new Date())

const {
  variantSelectId,
  resolvedVariantSku,
  variantSelectionValue,
  onVariantGroupChange,
  isVariantGroupValueOrderable,
} = usePublicMenuVariantSelect({
  displayGroups,
  variantSkus,
  variantOptionGroups,
})

function variantMinPrice(item) {
  const skus = variantSkus(item)
  if (!skus.length) return null
  const prices = skus.map((s) => (s.price != null ? Number(s.price) : null)).filter((p) => p != null)
  return prices.length ? Math.min(...prices) : null
}

function tagStyle(tag) {
  const hex = tag?.color || '#64748b'
  return { '--tag-bg': `${hex}20`, '--tag-fg': hex }
}

function onItemClick(item, categoryName, event) {
  let sku = null
  if (item?.type === 'with_variants') {
    sku = resolvedVariantSku(item)
  }
  emit('select-item', {
    item,
    categoryName: categoryName || 'Menu',
    sku,
    trigger: event?.currentTarget ?? null,
  })
}

function categoryUnavailableNow(availability) {
  return availability != null && typeof availability === 'object' && !isAvailableNow(availability, now.value)
}

function itemUnavailableNow(availability) {
  return availability != null && typeof availability === 'object' && !isAvailableNow(availability, now.value)
}

function isItemClickable(item, group) {
  if (!item || !group) return false
  if (categoryUnavailableNow(group.availability)) return false
  if (item.is_available === false) return false
  if (itemUnavailableNow(item.availability)) return false
  if (item.type === 'with_variants') {
    const sku = resolvedVariantSku(item)
    if (!sku || !isVariantSkuPubliclyOrderable(sku)) return false
  }
  return true
}

function onGroupTabClick(group, gIdx) {
  activeGroupIndex.value = gIdx
  if (categoryUnavailableNow(group?.availability)) {
    showUnavailableCategoryToast(group, now)
  }
}

function itemCardDisplayImageUrl(item) {
  if (!item) return null
  if (item.type === 'with_variants') {
    const sku = resolvedVariantSku(item)
    return sku?.image_url ?? sku?.imageUrl ?? itemCardImageUrl(item)
  }
  return itemCardImageUrl(item)
}
</script>

<style scoped>
.filter-btn {
  border-color: inherit;
}
.data-row {
  cursor: default;
}
.rms-t5-table-action {
  cursor: pointer;
}
.rms-t5-variant-select {
  appearance: auto;
}
.rms-public-tag {
  background-color: var(--tag-bg);
}
.rms-item-unavailable {
  filter: grayscale(0.7) blur(0.5px);
  opacity: 0.7;
}
.rms-item-unavailable .rms-t5-table-action {
  cursor: not-allowed;
}
</style>
